import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import AddQuestions from '../../AddQuestions/AddQuestions';
import { generateMinutes } from '../../../constants/hours';
import { getMinimunScreeningTimeInSeconds } from '../../../utils/time';
import { t } from '../../../i18n/i18n';
import TestGroupsItem from '../../TestGroupsItem/TestGroupsItem';
import Accordion from '../../Accordion/Accordion';
import Arrow from '../../../assets/modal/Arrow';
import NewStyleSelect from '../../NewStyleSelect/NewStyleSelect';
import './AditionalQuestions.scss';
import { FaTrashAlt } from 'react-icons/fa';
import useCreateScreening from '../../../hooks/useCreateScreening';
import AdditionalQuestionsWithTabs from './AdditionalQuestionsWithTabs';
import ViewQuestionModal from './ViewQuestionModal';
import { setNewScreeningData } from '../../../redux/actions/screeningActions';

export default function AditionalQuestions({
  newScreening,
  incompleteQuestion,
  setEditData,
  handleDelete,
  handleCustomScreeningsTime,
}) {
  const dispatch = useDispatch();
  const {
    handleDeleteChallengeTestGroup,
    fetchAdditionalQuestions,
    searchAdditionalQuestionsText,
    handleDeleteAdditionalQuestion,
    handleEditAdditionalTestQuestion,
    handleAddQuestion,
  } = useCreateScreening();

  const challengeName = useMemo(() => {
    const realName =
      newScreening?.customChallengeTestGroup?.[0]?.testFiles?.[0]?.displayName ??
      newScreening?.customChallengeTestGroup?.[0]?.testFiles?.[0]?.name;

    if (!realName) return '';
    return `${realName.slice(0, 40)}${realName.length > 40 ? '...' : ''}`;
  }, [newScreening]);

  const [showDropwdown, setShowDropdown] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [viewQuestionModal, setViewQuestionModal] = useState({
    visible: false,
    question: null,
  });

  const clearQuestionForm = () => {
    dispatch(
      setNewScreeningData({
        ...(newScreening ?? {}),
        isEdit: false,
        currentQuestionData: undefined,
      })
    );
    setShowAddQuestion(false);
  };

  const handleViewQuestion = (question) => {
    setViewQuestionModal({
      visible: true,
      question,
    });
  };

  const handleCloseViewModal = () => {
    setViewQuestionModal({
      visible: false,
      question: null,
    });
  };

  const minCustomScreeningsTimeInSeconds = getMinimunScreeningTimeInSeconds(newScreening);

  const minCustomScreeningsTimeInHours = minCustomScreeningsTimeInSeconds
    ? minCustomScreeningsTimeInSeconds / 3600
    : 0;

  const minutes = generateMinutes(5, 2.05, minCustomScreeningsTimeInHours ?? null);

  useEffect(() => {
    fetchAdditionalQuestions();
  }, []);

  return (
    <div data-testid="create-evaluation-third-step" className="d-flex flex-column">
      <AdditionalQuestionsWithTabs
        additionalQuestions={newScreening?.additionalQuestionsCompanyOptions || []}
        customTestGroup={newScreening?.customTestGroup || []}
        onAddQuestion={(questions, flags = {}) => {
          handleAddQuestion(false, false, questions, flags);
          setShowDropdown(true);
        }}
        onDeleteQuestion={handleDeleteAdditionalQuestion}
        onEditQuestion={handleEditAdditionalTestQuestion}
        onSearchQuestions={searchAdditionalQuestionsText}
        onClearSearch={fetchAdditionalQuestions}
        onShowAddQuestion={(value) => {
          if (typeof value === 'boolean') {
            setShowAddQuestion(value);
            if (value && newScreening?.isEdit) {
              clearQuestionForm();
            }
          } else {
            setShowAddQuestion(value.show);
            if (value.questionToEdit) {
              setEditData(value.questionToEdit);
            }
          }
        }}
        onTabChange={() => {
          if (showAddQuestion) {
            clearQuestionForm();
          }
        }}
      />
      {showAddQuestion && (
        <div className="d-flex justify-content-center">
          <AddQuestions
            incompleteQuestion={incompleteQuestion}
            setShowDropdown={(value) => {
              if (value) {
                setShowDropdown(true);
                setShowAddQuestion(false);
              }
            }}
          />
        </div>
      )}
      {newScreening?.customChallengeTestGroup?.length > 0 ? (
        <div className="bg-blue-principal w-100 d-flex justify-content-between align-items-center p-2 color-white font-weight-600 container-height mb-3">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex gap-2">
              <span>{t('CREATE_SCREENING_YOUR_CHALLENGE_ASYNC_QUESTION')}</span>
              <span>{`${t(newScreening?.customChallengeTestGroup?.[0]?.promptSubType)}`}</span>
              <span>{challengeName}</span>
            </div>
            <button
              onClick={handleDeleteChallengeTestGroup}
              type="button"
              className="border-0 outline-none cursor-pointer bg-blue-principal mr-2"
              data-testid="tg-item-delete-btn"
            >
              <FaTrashAlt color="#FFFFFF" size="1rem" />
            </button>
          </div>
        </div>
      ) : (
        ''
      )}
      <div className="bg-blue-principal w-100 d-flex justify-content-between align-items-center p-2 cursor-pointer color-white font-weight-600 container-height">
        <div className="d-flex align-items-center">
          <span>{`${newScreening?.customTestGroup?.length} ${t(
            'CREATE_SCREENING_YOUR_QUESTIONS_ADDED_QUESTIONS'
          )}`}</span>
          <NewStyleSelect
            error={false}
            className="select-width-third-step text-center justify-content-center"
            value={newScreening?.customScreeningTGDuration ?? '0 min'}
            options={minutes}
            onChange={(e) => {
              handleCustomScreeningsTime({ id: e.target.value });
            }}
            name="customScreeningTGDuration"
            borderClasses="border-white-select text-center text-white"
            textOptions="text-gray-2"
          />
          <span>{`${t('CREATE_EVALUATION_THIRD_STEP_CUSTOM_SET_TIME')}`}</span>
        </div>
        <div onClick={() => setShowDropdown(!showDropwdown)}>
          <Arrow
            className={`mr-2 ${
              showDropwdown ? 'arrow-dropdown-modal-collapsed' : 'arrow-dropdown-modal-uncollapsed'
            }`}
          />
        </div>
      </div>
      <div className="row pt-4 pb-2">
        {showDropwdown &&
          newScreening.customTestGroup
            .sort((a, b) => a.order - b.order)
            .map((item, index) => (
              <div className="col-3 mb-3" key={`add-question-${index}`}>
                <TestGroupsItem
                  test={item}
                  key={item.id}
                  disableOrderUp={index === 0}
                  handleEdit={
                    item.fromSubskills || item.isReadOnly
                      ? null
                      : () => {
                          setEditData(item);
                          setShowAddQuestion(true); // Abrir formulario de edición
                        }
                  }
                  handleView={
                    item.fromSubskills || item.isReadOnly
                      ? () => handleViewQuestion(item)
                      : null
                  }
                  disableOrderDown={index === newScreening.customTestGroup.length - 1}
                  handleDelete={() => handleDelete(item.deleteId)}
                  isComplementaryQuestion
                />
              </div>
            ))}
      </div>

      {/* Modal de visualización */}
      <ViewQuestionModal
        visible={viewQuestionModal.visible}
        question={viewQuestionModal.question}
        onClose={handleCloseViewModal}
      />
      <Accordion
        title={t('CREATE_EVALUATION_THIRD_STEP_CUSTOM_SET_TIME')}
        className="mt-3"
        open={showDropwdown}
      ></Accordion>
    </div>
  );
}
